import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setSelectedStatement,
  markStatementCompleted,
} from "../../../redux/Commission/commissionStatementSlice";

const NAVY = "#205393";
const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const ORA = "#c05621";
const ORAL = "#fff8f0";
const ACC = "#1e4a8a";
const ACCL = "#e8f0fb";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const GRN = "#2e7d32";
const GRNL = "#f0fdf4";

interface Props {
  onOpenAll: () => void;
}

const SessionResumePanel: React.FC<Props> = ({ onOpenAll }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { statements, selectedStatementId, loading } = useSelector(
    (s: RootState) => s.commissionStatement
  );
  const [completeTarget, setCompleteTarget] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [accordionOpen, setAccordionOpen] = useState(false);

  const filtered = statements.filter((s) => {
    const st = (s.Status || "").toUpperCase().replace(/[\s-]/g, "_");
    return st === "IN_PROGRESS";
  });

  if (loading.statements) return <Spinner size="small" label="Checking uploads..." />;
  if (statements.length === 0) return null;

  const handleSelect = (statementId: string) => {
    dispatch(setSelectedStatement(selectedStatementId === statementId ? null : statementId));
  };

  const confirmComplete = async () => {
    if (!completeTarget) return;
    const result = await dispatch(markStatementCompleted({ statementId: completeTarget }));
    if (markStatementCompleted.fulfilled.match(result)) {
      setCompleteTarget(null);
      setCompleteError(null);
    } else {
      setCompleteError((result as any).payload as string || "Failed to mark as completed. Please try again.");
    }
  };

  const handleDialogClose = () => {
    setCompleteTarget(null);
    setCompleteError(null);
  };

  return (
    <div style={{ marginBottom: "14px", border: `1px solid ${BRD}`, borderRadius: "5px", overflow: "hidden" }}>
      {/* Accordion header */}
      <button
        onClick={() => setAccordionOpen((o) => !o)}
        aria-expanded={accordionOpen}
        aria-controls="session-resume-body"
        style={{
          width: "100%", background: ACCL, border: "none", padding: "9px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px",
          cursor: "pointer", borderBottom: accordionOpen ? `1px solid ${BRD}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ACC} strokeWidth="2.2">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <span style={{ fontSize: "12px", fontWeight: 600, color: ACC }}>
            In Progress uploads — select one to resume
          </span>
          <span style={{ fontSize: "11px", fontWeight: 700, background: ACC, color: "#fff", borderRadius: "20px", padding: "1px 8px" }}>
            {filtered.length}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            onClick={(e) => { e.stopPropagation(); onOpenAll(); }}
            style={{ background: "#fff", color: ACC, border: `1px solid ${ACC}`, borderRadius: "5px", fontSize: "11px", height: "24px", padding: "0 10px", cursor: "pointer", fontWeight: 500 }}
          >
            All Uploads
          </button>
          <svg
            viewBox="0 0 24 24" fill="none" stroke={ACC} strokeWidth="2.5" width="13" height="13"
            style={{ transform: accordionOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      {/* Accordion body */}
      {accordionOpen && (
        <div id="session-resume-body" style={{ padding: "10px 14px" }}>
      {filtered.length === 0 && (
        <p style={{ fontSize: "12px", color: T3, margin: "8px 0" }}>
          No in-progress uploads. Use <strong>All Uploads</strong> to view completed statements.
        </p>
      )}

      {filtered.map((s) => {
        const isCompleted = s.Status === "COMPLETED";
        const remaining = (s.TotalRows ?? 0) - (s.ReceiptedRows ?? 0);
        const isSelected = s.CommissionStatementId === selectedStatementId;
        return (
          <div
            key={s.CommissionStatementId}
            role="button"
            tabIndex={isCompleted ? -1 : 0}
            aria-pressed={isSelected}
            onClick={() => !isCompleted && handleSelect(s.CommissionStatementId)}
            onKeyDown={(e) => { if (!isCompleted && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleSelect(s.CommissionStatementId); } }}
            style={{
              border: isCompleted ? `1px solid ${GRN}` : isSelected ? `1px solid ${NAVY}` : `1px solid ${BRD}`,
              borderRadius: "5px",
              padding: "12px 16px",
              marginBottom: "8px",
              cursor: isCompleted ? "default" : "pointer",
              transition: "all 0.15s",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              background: isCompleted ? GRNL : isSelected ? ACCL : "#fff",
              boxShadow: isSelected ? `0 0 0 2px ${NAVY}` : "none",
              opacity: isCompleted ? 0.85 : 1,
            }}
          >
            {/* Status indicator */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {isCompleted ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GRN} strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
                </svg>
              ) : (
                <div style={{ width: "16px", height: "16px", borderRadius: "50%", border: `2px solid ${isSelected ? NAVY : BRD2}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                  {isSelected && <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: NAVY }} />}
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: T1, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                📄 {s.FileName || "Unknown file"}
                {isCompleted ? (
                  <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: GRNL, color: GRN, border: `1px solid ${GRN}` }}>
                    Completed
                  </span>
                ) : (
                  <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: ORAL, color: ORA }}>
                    {remaining} remaining
                  </span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: T3, marginTop: "3px" }}>
                Uploaded {new Date(s.CreatedAt).toLocaleDateString("en-GB")} &nbsp;·&nbsp;
                {s.ReceiptedRows ?? 0} of {s.TotalRows ?? 0} receipted
              </div>
            </div>

            {/* Actions — hidden for completed */}
            {!isCompleted && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSelect(s.CommissionStatementId); }}
                  style={{ background: "#fff", color: isSelected ? NAVY : T2, border: `1px solid ${isSelected ? NAVY : BRD2}`, borderRadius: "5px", fontSize: "11px", height: "26px", padding: "0 10px", cursor: "pointer", fontWeight: isSelected ? 700 : 500 }}
                >
                  {isSelected ? "Selected ✓" : "Select"}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCompleteTarget(s.CommissionStatementId); }}
                  disabled={loading.markComplete}
                  style={{ background: GRN, color: "#fff", border: "none", borderRadius: "5px", fontSize: "11px", height: "26px", padding: "0 8px", cursor: "pointer" }}
                >
                  Mark Complete
                </button>
              </div>
            )}
          </div>
        );
      })}

      {selectedStatementId && (
        <button
          onClick={() => dispatch(setSelectedStatement(null))}
          style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12px", height: "28px", padding: "0 12px", cursor: "pointer", marginTop: "4px" }}
        >
          + Start New Upload Instead
        </button>
      )}
        </div>
      )}

      <Dialog open={!!completeTarget} onOpenChange={handleDialogClose}>
        <DialogSurface>
          <DialogTitle>Mark as Completed</DialogTitle>
          <DialogBody>
            <div>You will no longer be able to resume this upload. Are you sure?</div>
            {completeError && (
              <div style={{ marginTop: "10px", fontSize: "12px", color: "#c92a2a", background: "#ffe6e6", border: "1px solid #c92a2a", borderRadius: "4px", padding: "7px 10px" }}>
                {completeError}
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <button
              onClick={confirmComplete}
              disabled={loading.markComplete}
              style={{ background: ACC, color: "#fff", border: "none", borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 16px", height: "32px", cursor: loading.markComplete ? "not-allowed" : "pointer" }}
            >
              {loading.markComplete ? "Saving…" : "Mark as Completed"}
            </button>
            <button
              onClick={handleDialogClose}
              disabled={loading.markComplete}
              style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: loading.markComplete ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SessionResumePanel;
